/* eslint-disable no-console -- HTTP error and retry diagnostics */
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { NotificationService } from '../services/infrastructure/notification/notification.service';
import { API_CONSTANTS } from '../constants/api.constants';
import { environment } from '@environment/environment';
import { ERROR_INTERCEPTOR_MESSAGES } from '../constants/error-interceptor.constants';

type RetryableStatusCode = (typeof API_CONSTANTS.RETRYABLE_STATUS_CODES)[number];

/**
 * HTTP Interceptor for centralized error handling
 * Automatically handles errors, retries, and user notifications
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    private readonly retryDelay = API_CONSTANTS.RETRY.DELAY_MS;
    private readonly safeMethods = API_CONSTANTS.SAFE_HTTP_METHODS;
    private readonly backoffMultiplier = API_CONSTANTS.RETRY.BACKOFF_MULTIPLIER;
    private readonly maxRetryDelay = API_CONSTANTS.RETRY.MAX_DELAY_MS;

    constructor(private notificationService: NotificationService) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const retryHeader = request.headers.get('X-Retry-Count');
        const retryLimit =
            retryHeader !== null && !isNaN(Number(retryHeader)) ? Number(retryHeader) : API_CONSTANTS.RETRY.MAX_COUNT;

        return next.handle(request).pipe(
            // Retry logic for retryable errors
            retry({
                count: retryLimit,
                delay: (error: HttpErrorResponse, retryCount: number) => {
                    if (this.shouldRetry(error, request.method)) {
                        const delayMs = this.calculateRetryDelay(retryCount);
                        if (!environment.production) {
                            this.logRetryAttempt(request, error, retryCount, delayMs);
                        }
                        return timer(delayMs);
                    }

                    // Don't retry, throw error immediately
                    return throwError(() => error);
                },
            }),

            // Global error handling
            catchError((error: HttpErrorResponse) => {
                // Handle the error
                return this.handleError(error, request);
            })
        );
    }

    /**
     * Determine if request should be retried
     */
    private shouldRetry(error: HttpErrorResponse, method: string): boolean {
        // Only retry safe HTTP methods
        if (!this.safeMethods.includes(method.toUpperCase())) {
            return false;
        }

        // Only retry specific status codes
        if (!API_CONSTANTS.RETRYABLE_STATUS_CODES.includes(error.status as RetryableStatusCode)) {
            return false;
        }

        // Don't retry if error response explicitly says not to
        if (error.headers.get('X-No-Retry') === 'true') {
            return false;
        }

        return true;
    }

    /**
     * Calculate retry delay with exponential backoff and jitter
     */
    private calculateRetryDelay(retryCount: number): number {
        // Exponential backoff: 1s, 2s, 4s, 8s...
        const baseDelay = this.retryDelay * Math.pow(this.backoffMultiplier, retryCount - 1);

        // Add jitter (±20% of base delay)
        const jitter = Math.random() * 0.4 - 0.2; // -20% to +20%
        const jitterDelay = baseDelay * (1 + jitter);

        // Cap maximum delay at 30 seconds
        return Math.min(jitterDelay, this.maxRetryDelay);
    }

    /**
     * Handle HTTP errors comprehensively
     */
    private handleError(error: HttpErrorResponse, request: HttpRequest<unknown>): Observable<never> {
        if (!this.shouldSkipNotification(error, request)) {
            if (!environment.production) {
                this.logError(error, request);
            }

            this.showUserNotification(error, request);
        }

        // Return enhanced error with additional metadata
        const context = this.getRequestContext(request);
        const enhancedError = this.enhanceError(error, context);
        return throwError(() => enhancedError);
    }

    /**
     * Get request context for error reporting
     */
    private getRequestContext(request: HttpRequest<unknown>): string {
        const url = new URL(request.url, window.location.origin);
        return `${request.method} ${url.pathname}${url.search}`;
    }

    /**
     * Log error with detailed information
     */
    private logError(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
        const errorInfo = {
            status: error.status,
            statusText: error.statusText,
            url: request.url,
            method: request.method,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            errorBody: error.error,
            requestHeaders: this.sanitizeHeaders(request.headers),
            responseHeaders: this.sanitizeHeaders(error.headers),
        };

        if (error.status >= 500) {
            console.error('🚨 Server Error:', errorInfo);
        } else if (error.status >= 400) {
            console.warn('⚠️ Client Error:', errorInfo);
        } else {
            console.log('ℹ️ Network Error:', errorInfo);
        }
    }

    /**
     * Show appropriate user notification
     */
    private showUserNotification(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
        const message = this.getUserFriendlyMessage(error, request);
        const severity = this.getNotificationSeverity(error.status);

        // Show notification
        switch (severity) {
            case 'error':
                this.notificationService.error(message);
                break;
            case 'warning':
                this.notificationService.warning(message);
                break;
            default:
                this.notificationService.info(message);
        }
    }

    /**
     * Get user-friendly error message
     */
    private getUserFriendlyMessage(error: HttpErrorResponse, request: HttpRequest<unknown>): string {
        // Try to extract message from error response
        const serverMessage = error.error?.message || error.error?.error?.message;
        if (serverMessage && typeof serverMessage === 'string') {
            return serverMessage;
        }

        // Context-aware messages based on request URL
        const url = request.url.toLowerCase();
        const status = error.status.toString();

        if (url.includes(API_CONSTANTS.ENDPOINTS.TRAINING)) {
            return ERROR_INTERCEPTOR_MESSAGES.TRAINING[status] || ERROR_INTERCEPTOR_MESSAGES.TRAINING['DEFAULT'];
        }

        if (url.includes(API_CONSTANTS.ENDPOINTS.ADMIN.DATASET)) {
            return ERROR_INTERCEPTOR_MESSAGES.DATASET[status] || ERROR_INTERCEPTOR_MESSAGES.DATASET['DEFAULT'];
        }

        // Generic messages by status code
        return ERROR_INTERCEPTOR_MESSAGES.STATUS[status] || ERROR_INTERCEPTOR_MESSAGES.DEFAULT;
    }

    /**
     * Get notification severity based on HTTP status
     */
    private getNotificationSeverity(status: number): 'error' | 'warning' | 'info' {
        if (status >= 500) return 'error';
        if (status >= 400) return 'warning';
        return 'info';
    }

    /**
     * Determine if notification should be skipped
     */
    private shouldSkipNotification(error: HttpErrorResponse, request: HttpRequest<unknown>): boolean {
        // Skip notifications for background requests
        if (request.headers.has('X-Silent-Error') || error.error.silent) {
            return true;
        }

        // Skip for certain status codes that don't need user notification
        if (API_CONSTANTS.SILENT_STATUS_CODES.includes(error.status)) {
            return true;
        }

        // Skip for polling or health check requests
        if (API_CONSTANTS.SILENT_URLS.some((url) => request.url.includes(url))) {
            return true;
        }

        return false;
    }

    /**
     * Enhance error with additional metadata
     */
    private enhanceError(error: HttpErrorResponse, context: string): HttpErrorResponse {
        const enhancedError = new HttpErrorResponse({
            error: {
                ...error.error,
                context,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                url: error.url,
                originalError: error.error,
                silent: error.error['silent'],
                redirectTo: error.error['redirectTo'],
            },
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url || undefined,
        });

        return enhancedError;
    }

    /**
     * Log retry attempts
     */
    private logRetryAttempt(
        request: HttpRequest<unknown>,
        error: HttpErrorResponse,
        retryCount: number,
        delayMs: number
    ): void {
        console.warn(`🔄 Retrying request (${retryCount}) after ${delayMs}ms:`, {
            url: request.url,
            method: request.method,
            status: error.status,
            statusText: error.statusText,
        });
    }

    /**
     * Sanitize headers for logging (remove sensitive information)
     */
    private sanitizeHeaders(headers: HttpHeaders): Record<string, string> {
        const sanitized: Record<string, string> = {};

        if (headers?.keys) {
            headers.keys().forEach((key: string) => {
                const lowerKey = key.toLowerCase();
                if (API_CONSTANTS.SENSITIVE_HEADERS.includes(lowerKey)) {
                    sanitized[key] = '[REDACTED]';
                } else {
                    sanitized[key] = headers.get(key) || '';
                }
            });
        }

        return sanitized;
    }
}
